'use client';

import { useState, useEffect } from 'react';
import { Webhook, Plus, Trash2, Save, Bug, AlertCircle, Lock, ChevronRight, Loader2, X } from 'lucide-react';
import { useTranslation } from '@/providers/I18nProvider';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useAgent } from '@/hooks/useAgent';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/useToast';

interface WebhookConfig {
  id: string;
  name: string;
  url: string;
  enabled: boolean;
  events: string[];
  headers?: Record<string, string>;
  secret?: string;
}

interface WebhookSettingsProps {
  agentId: string;
  agentName: string;
}

// Define webhook events without labels - they'll be translated in the component
const WEBHOOK_EVENTS = [
  // Specific error types
  { id: 'error.llm' },
  { id: 'error.tool' },
  { id: 'feedback.user' },
  
  // Security & Safety events
  { id: 'threat.detection' },
  { id: 'abuse.detection' },
  
  // Catch-all categories
  { id: 'security.all' },
  { id: 'error.all' },
];

export default function WebhookSettings({ agentId, agentName }: WebhookSettingsProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  
  // Helper function to get webhook event translation keys
  const getEventTranslation = (eventId: string) => {
    const eventMap: Record<string, { label: string; description: string }> = {
      'error.llm': {
        label: t('admin.webhooks.events.llm.label'),
        description: t('admin.webhooks.events.llm.description'),
      },
      'error.tool': {
        label: t('admin.webhooks.events.tool.label'),
        description: t('admin.webhooks.events.tool.description'),
      },
      'feedback.user': {
        label: t('admin.webhooks.events.userFeedback.label'),
        description: t('admin.webhooks.events.userFeedback.description'),
      },
      'threat.detection': {
        label: t('admin.webhooks.events.threat.label'),
        description: t('admin.webhooks.events.threat.description'),
      },
      'abuse.detection': {
        label: t('admin.webhooks.events.abuse.label'),
        description: t('admin.webhooks.events.abuse.description'),
      },
      'error.all': {
        label: t('admin.webhooks.events.all.label'),
        description: t('admin.webhooks.events.all.description'),
      },
      'security.all': {
        label: t('admin.webhooks.events.securityAll.label'),
        description: t('admin.webhooks.events.securityAll.description'),
      },
    };
    return eventMap[eventId] || { label: eventId, description: '' };
  };
  const [saving, setSaving] = useState(false);
  const [testingWebhook, setTestingWebhook] = useState<string | null>(null);
  const [editingWebhook, setEditingWebhook] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newWebhook, setNewWebhook] = useState<Partial<WebhookConfig>>({
    name: '',
    url: '',
    enabled: true,
    events: ['error.all'],
    headers: {},
  });
  const [testResults, setTestResults] = useState<Record<string, { success: boolean; message: string }>>({});
  const [localWebhooks, setLocalWebhooks] = useState<WebhookConfig[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [webhookToDelete, setWebhookToDelete] = useState<WebhookConfig | null>(null);
  const [deletingWebhook, setDeletingWebhook] = useState(false);
  const [savingWebhookToggle, setSavingWebhookToggle] = useState<string | null>(null);

  // Use feature flags hook
  const { chatWebhooksEnabled, isLoading: loadingFlags } = useFeatureFlags(agentId);

  // Use agent hook
  const { configDoc, isLoading: loadingAgent, updateAgent, refetch, agent } = useAgent(agentId);
  const organizationId = configDoc?.organizationId;
  
  // Get webhooks from agent data
  const webhooks = configDoc.webhooks || [];
  const loading = loadingAgent;

  // Sync local state with server state when it changes
  useEffect(() => {
    if (!loadingAgent && configDoc.webhooks) {
      setLocalWebhooks(configDoc.webhooks);
    }
  }, [configDoc.webhooks, loadingAgent]);

  // Update webhooks through agent hook
  const setWebhooks = async (newWebhooks: WebhookConfig[]) => {
    await updateAgent.mutateAsync({ webhooks: newWebhooks });
  };

  const saveWebhooksToServer = async (webhooksToSave: WebhookConfig[]) => {
    try {
      setSaving(true);
      // Update local state immediately for instant UI feedback
      setLocalWebhooks(webhooksToSave);
      
      await setWebhooks(chatWebhooksEnabled ? webhooksToSave : []);
      
      // Invalidate and refetch to ensure we have the latest data
      await queryClient.invalidateQueries({ queryKey: ['agent', agentId] });
      
      showToast('Webhook saved successfully', 'success');
    } catch (error) {
      console.error('Failed to save webhooks:', error);
      showToast('Failed to save webhook', 'error');
      // Revert local state on error
      setLocalWebhooks(webhooks);
    } finally {
      setSaving(false);
    }
  };

  const addWebhook = async () => {
    if (!newWebhook.name || !newWebhook.url) return;

    const webhook: WebhookConfig = {
      id: `webhook_${Date.now()}`,
      name: newWebhook.name,
      url: newWebhook.url,
      enabled: newWebhook.enabled ?? true,
      events: newWebhook.events || ['error.all'],
      headers: newWebhook.headers,
      secret: newWebhook.secret,
    };

    const updatedWebhooks = [...localWebhooks, webhook];
    
    // Clear form and close modal immediately for better UX
    setNewWebhook({
      name: '',
      url: '',
      enabled: true,
      events: ['error.all'],
      headers: {},
    });
    setShowAddForm(false);
    
    // Auto-save
    await saveWebhooksToServer(updatedWebhooks);
  };

  const updateWebhook = async (id: string, updates: Partial<WebhookConfig>) => {
    const updatedWebhooks = localWebhooks.map(w => w.id === id ? { ...w, ...updates } : w);
    await saveWebhooksToServer(updatedWebhooks);
  };

  const handleDeleteWebhook = async () => {
    if (!webhookToDelete) return;
    
    setDeletingWebhook(true);
    try {
      const updatedWebhooks = localWebhooks.filter(w => w.id !== webhookToDelete.id);
      
      // Update local state immediately
      setLocalWebhooks(updatedWebhooks);
      
      // Save to server
      await setWebhooks(chatWebhooksEnabled ? updatedWebhooks : []);
      
      // Invalidate cache
      await queryClient.invalidateQueries({ queryKey: ['agent', agentId] });
      
      showToast(t('admin.webhooks.delete.success'), 'success');
      setShowDeleteModal(false);
      setWebhookToDelete(null);
    } catch (error) {
      console.error('Failed to delete webhook:', error);
      showToast(t('admin.webhooks.delete.failed'), 'error');
      // Revert local state on error
      setLocalWebhooks(webhooks);
    } finally {
      setDeletingWebhook(false);
    }
  };
  
  const openDeleteConfirmation = (webhook: WebhookConfig) => {
    setWebhookToDelete(webhook);
    setShowDeleteModal(true);
  };

  const testWebhook = async (webhook: WebhookConfig) => {
    setTestingWebhook(webhook.id);
    try {
      const response = await fetch(`/api/chat/agents/${agentId}/webhooks/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          webhook,
          testPayload: {
            event: 'error.test',
            timestamp: new Date().toISOString(),
            agent: {
              id: agentId,
              name: agentName,
            },
            error: {
              id: 'test_error_' + Date.now(),
              type: 'TEST_ERROR',
              message: 'This is a test error from webhook configuration',
              details: {
                test: true,
                source: 'webhook_configuration',
              },
            },
          },
        }),
      });

      const result = await response.json();
      setTestResults({
        ...testResults,
        [webhook.id]: {
          success: result.success,
          message: result.message || (result.success ? 'Webhook test successful!' : 'Webhook test failed'),
        },
      });
    } catch (error) {
      setTestResults({
        ...testResults,
        [webhook.id]: {
          success: false,
          message: 'Failed to test webhook',
        },
      });
    } finally {
      setTestingWebhook(null);
    }
  };

  // Show skeleton loader while loading feature flags
  if (loadingFlags) {
    return (
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto p-8">
          {/* Header skeleton */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-2">
              <div className="h-8 w-32 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
              <div className="h-5 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
            </div>
            <div className="h-4 w-96 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
          </div>

          {/* Webhook cards skeleton */}
          <div className="space-y-4">
            {[1, 2].map((index) => (
              <div key={index} className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="h-6 w-32 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
                      <div className="h-6 w-11 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse"></div>
                    </div>
                    <div className="h-4 w-64 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-8 w-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                    <div className="h-8 w-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                    <div className="h-8 w-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                  </div>
                </div>
              </div>
            ))}
            
            {/* Webhook payload example skeleton */}
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 p-6">
              <div className="flex items-start gap-3">
                <div className="h-5 w-5 bg-blue-200 dark:bg-blue-700 rounded animate-pulse mt-0.5"></div>
                <div className="flex-1">
                  <div className="h-5 w-40 bg-blue-200 dark:bg-blue-700 rounded-lg animate-pulse mb-2"></div>
                  <div className="h-48 w-full bg-blue-100 dark:bg-blue-900/30 rounded animate-pulse"></div>
                  <div className="h-3 w-3/4 bg-blue-200 dark:bg-blue-700 rounded-lg animate-pulse mt-2"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-4xl mx-auto p-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              {t('admin.webhooks.title')}
            </h1>
            {!chatWebhooksEnabled && (
              <span className="text-xs px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 rounded">
                {t('admin.sidebar.badges.proEnterprise')}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t('admin.webhooks.description')}
          </p>
        </div>
        

        {/* Actions Bar - only show when there are webhooks */}
        {localWebhooks.length > 0 && (
          <div className="mb-6 flex justify-end">
            <button
              onClick={() => chatWebhooksEnabled && setShowAddForm(true)}
              disabled={!chatWebhooksEnabled}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 dark:bg-blue-500 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="h-4 w-4" />
              {t('admin.webhooks.addWebhook')}
            </button>
          </div>
        )}

        {loading ? (
          <div className="space-y-4">
            {/* Skeleton for webhook cards */}
            {[1, 2].map((index) => (
              <div key={index} className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6 animate-pulse">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <div className="h-6 w-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
                      <div className="h-6 w-11 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                    </div>
                    <div className="h-4 w-64 bg-gray-200 dark:bg-gray-700 rounded mt-2"></div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-8 w-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    <div className="h-8 w-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    <div className="h-8 w-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  </div>
                </div>
              </div>
            ))}
            
            {/* Skeleton for webhook payload example */}
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 p-6 animate-pulse">
              <div className="flex items-start gap-3">
                <div className="h-5 w-5 bg-blue-200 dark:bg-blue-700 rounded mt-0.5"></div>
                <div className="flex-1">
                  <div className="h-5 w-40 bg-blue-200 dark:bg-blue-700 rounded mb-2"></div>
                  <div className="h-48 w-full bg-blue-100 dark:bg-blue-900/30 rounded"></div>
                  <div className="h-3 w-3/4 bg-blue-200 dark:bg-blue-700 rounded mt-2"></div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Webhook List */}
            {localWebhooks.length === 0 && !showAddForm ? (
              !chatWebhooksEnabled ? (
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-lg border border-amber-200 dark:border-amber-800 p-8">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 p-3 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                      <Webhook className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                        {t('upgradeCTA.webhooks.title')}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        {t('upgradeCTA.webhooks.description')}
                      </p>
                      <div className="flex items-center gap-4">
                        <a
                          href={`${process.env.NEXT_PUBLIC_VECTORIZE_BASE_URL || 'https://app.vectorize.io'}/organization/${organizationId}/admin/billing/changeplan`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-lg transition-colors text-sm"
                        >
                          {t('upgradeCTA.upgradeToPro')}
                          <ChevronRight className="h-4 w-4" />
                        </a>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {t('upgradeCTA.webhooks.features')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-8 text-center">
                  <Webhook className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                    No webhooks configured
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                    Add webhooks to receive real-time notifications when errors occur in your agent.
                  </p>
                  <button
                    onClick={() => setShowAddForm(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 dark:bg-blue-500 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                    Add Your First Webhook
                  </button>
                </div>
              )
            ) : (
              <>
                {localWebhooks.map((webhook) => (
                  <div key={webhook.id} className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6 space-y-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                            {webhook.name}
                          </h3>
                          <button
                            type="button"
                            onClick={async () => {
                              if (chatWebhooksEnabled && savingWebhookToggle !== webhook.id) {
                                const newValue = !webhook.enabled;
                                setSavingWebhookToggle(webhook.id);
                                
                                // Update local state immediately
                                setLocalWebhooks(prev => prev.map(w => 
                                  w.id === webhook.id ? { ...w, enabled: newValue } : w
                                ));
                                
                                try {
                                  await updateWebhook(webhook.id, { enabled: newValue });
                                } catch (err) {
                                  // Revert on error
                                  setLocalWebhooks(prev => prev.map(w => 
                                    w.id === webhook.id ? { ...w, enabled: !newValue } : w
                                  ));
                                  showToast(err instanceof Error ? err.message : t('common.errors.saveFailed'), 'error');
                                } finally {
                                  setSavingWebhookToggle(null);
                                }
                              }
                            }}
                            disabled={!chatWebhooksEnabled || savingWebhookToggle === webhook.id}
                            className={`
                              relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                              ${webhook.enabled ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'}
                              ${!chatWebhooksEnabled || savingWebhookToggle === webhook.id ? 'opacity-50 cursor-not-allowed' : ''}
                            `}
                          >
                            <span
                              className={`
                                inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                                ${webhook.enabled ? 'translate-x-6' : 'translate-x-1'}
                              `}
                            />
                          </button>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          {webhook.url}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => chatWebhooksEnabled && testWebhook(webhook)}
                          disabled={!chatWebhooksEnabled || testingWebhook === webhook.id}
                          className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        >
                          {testingWebhook === webhook.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Bug className="h-4 w-4" />
                          )}
                        </button>
                        <button
                          onClick={() => chatWebhooksEnabled && setEditingWebhook(webhook.id === editingWebhook ? null : webhook.id)}
                          disabled={!chatWebhooksEnabled}
                          className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => chatWebhooksEnabled && openDeleteConfirmation(webhook)}
                          disabled={!chatWebhooksEnabled}
                          className="text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                          title={t('admin.webhooks.actions.delete')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    {/* Test Result */}
                    {testResults[webhook.id] && (
                      <div className={`p-3 rounded-lg ${
                        testResults[webhook.id].success
                          ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                          : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'
                      }`}>
                        <p className="text-sm">{testResults[webhook.id].message}</p>
                      </div>
                    )}

                    {/* Edit Form */}
                    {editingWebhook === webhook.id && (
                      <div className="pt-4 border-t border-gray-200 dark:border-gray-700 space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Events to trigger on
                          </label>
                          <div className="space-y-2">
                            {WEBHOOK_EVENTS.map((event) => (
                              <label key={event.id} className="flex items-start gap-3">
                                <input
                                  type="checkbox"
                                  checked={webhook.events.includes(event.id)}
                                  onChange={async (e) => {
                                    if (!chatWebhooksEnabled) return;
                                    if (e.target.checked) {
                                      await updateWebhook(webhook.id, {
                                        events: [...webhook.events, event.id],
                                      });
                                    } else {
                                      await updateWebhook(webhook.id, {
                                        events: webhook.events.filter((ev: string) => ev !== event.id),
                                      });
                                    }
                                  }}
                                  disabled={!chatWebhooksEnabled}
                                  className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <div>
                                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                    {getEventTranslation(event.id).label}
                                  </p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">
                                    {getEventTranslation(event.id).description}
                                  </p>
                                </div>
                              </label>
                            ))}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Webhook Secret (optional)
                          </label>
                          <input
                            type="text"
                            value={webhook.secret || ''}
                            onChange={async (e) => chatWebhooksEnabled && await updateWebhook(webhook.id, { secret: e.target.value })}
                            disabled={!chatWebhooksEnabled}
                            placeholder="Used to sign webhook payloads"
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            If provided, payloads will include an X-Webhook-Signature header with HMAC-SHA256 signature
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {/* Add New Webhook Form */}
                {showAddForm && (
                  <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6 space-y-4">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                      Add New Webhook
                    </h3>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Name
                      </label>
                      <input
                        type="text"
                        value={newWebhook.name || ''}
                        onChange={(e) => chatWebhooksEnabled && setNewWebhook({ ...newWebhook, name: e.target.value })}
                        disabled={!chatWebhooksEnabled}
                        placeholder="Error Notification Webhook"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        URL
                      </label>
                      <input
                        type="url"
                        value={newWebhook.url || ''}
                        onChange={(e) => chatWebhooksEnabled && setNewWebhook({ ...newWebhook, url: e.target.value })}
                        disabled={!chatWebhooksEnabled}
                        placeholder="https://example.com/webhooks/errors"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => {
                          setShowAddForm(false);
                          setNewWebhook({
                            name: '',
                            url: '',
                            enabled: true,
                            events: ['error.all'],
                            headers: {},
                          });
                        }}
                        className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      >
                        {t('admin.webhooks.addModal.cancel')}
                      </button>
                      <button
                        onClick={() => chatWebhooksEnabled && addWebhook()}
                        disabled={!chatWebhooksEnabled || !newWebhook.name || !newWebhook.url || saving}
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 dark:bg-blue-500 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors disabled:opacity-50 flex items-center gap-2"
                      >
                        {saving ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            {t('common.saving')}
                          </>
                        ) : (
                          t('admin.webhooks.addModal.submitButton')
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Webhook Payload Example */}
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 p-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
                    {t('admin.webhooks.payload.title')}
                  </h4>
                  <pre className="text-xs text-blue-800 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30 p-3 rounded overflow-auto">
{`{
  "event": "error.llm",
  "timestamp": "2024-01-20T12:34:56.789Z",
  "agent": {
    "id": "agent_123",
    "name": "My Agent"
  },
  "error": {
    "id": "error_abc123",
    "type": "LLM_ERROR",
    "message": "Rate limit exceeded",
    "details": {
      "provider": "GROQ",
      "model": "llama-3.1-8b-instant",
      "statusCode": 429
    },
    "userId": "user_456",
    "conversationId": "conv_789"
  }
}`}</pre>
                  <p className="text-xs text-blue-700 dark:text-blue-400 mt-2">
                    {t('admin.webhooks.payload.description')}
                  </p>
                </div>
              </div>
            </div>

          </div>
        )}
      </div>
      
      {/* Delete Confirmation Modal */}
      {showDeleteModal && webhookToDelete && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <AlertCircle className="h-5 w-5 text-gray-400 dark:text-gray-500" strokeWidth={1.5} />
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  {t('admin.webhooks.delete.title')}
                </h2>
              </div>
              
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {t('admin.webhooks.delete.confirmation')}
              </p>
              
              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mb-6">
                <p className="font-medium text-gray-900 dark:text-gray-100">
                  {webhookToDelete.name}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {webhookToDelete.url}
                </p>
              </div>
              
              <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-lg p-3 mb-6">
                <div className="flex gap-2">
                  <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" strokeWidth={2} />
                  <p className="text-sm text-red-700 dark:text-red-300">
                    {t('admin.webhooks.delete.warning')}
                  </p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setWebhookToDelete(null);
                  }}
                  className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 
                           text-gray-700 dark:text-gray-300 font-medium rounded-lg transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleDeleteWebhook}
                  disabled={deletingWebhook}
                  className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg 
                           transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {deletingWebhook ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t('admin.webhooks.delete.deleting')}
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4" />
                      {t('admin.webhooks.delete.confirmButton')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}